/**
 * INIKRU "Shopfront" component kit — the designer portal's shared primitives.
 *
 * One identity with the inikru.com landing and the logo itself: paper canvas,
 * ink (#1B1B1B) frames, Montserrat display, Karla body, crimson (#B40A0A)
 * loud but scarce. Signature: the window card (.win-card / WindowCard) — a
 * panel drawn as a tiny browser window with three dots, straight from the
 * logo. Read INIKRU_UI.md in this folder before styling any page.
 */
import React from 'react';
import { motion } from 'framer-motion';

export const ORANGE = '#B40A0A';
export const ORANGE_DARK = '#8E0808';
export const INK = '#1B1B1B';
export const BONE = '#FAFAF8';   // paper
export const LINE = '#E7E7E3';
export const MUTED = '#63635E';
export const CREAM = '#F5E3DC';

/**
 * PageHeader — the editorial header every page starts with.
 * Big serif title, one-line purpose, ONE primary action on the right.
 */
export const PageHeader = ({ eyebrow, title, subtitle, action, className = '' }) => (
  <div className={`mb-6 sm:mb-8 ${className}`}>
    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
      <div className="min-w-0">
        {eyebrow && (
          <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-primary mb-2">{eyebrow}</p>
        )}
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-ink leading-[1.05]">{title}</h1>
        {subtitle && <p className="mt-2 text-[15px] text-muted max-w-xl">{subtitle}</p>}
      </div>
      {action && <div className="flex-shrink-0">{action}</div>}
    </div>
  </div>
);

/** Primary button — THE crimson. Use once per screen. */
export const PrimaryButton = ({ children, onClick, type = 'button', disabled, className = '' }) => (
  <motion.button
    type={type}
    onClick={onClick}
    disabled={disabled}
    whileHover={disabled ? {} : { scale: 1.02 }}
    whileTap={disabled ? {} : { scale: 0.98 }}
    className={`inline-flex items-center justify-center gap-2 rounded-[10px] px-6 py-3 text-[15px] font-display font-bold text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed hover:bg-primary-dark ${className}`}
    style={{ background: ORANGE }}
  >
    {children}
  </motion.button>
);

/** Quiet secondary button — white pill with a warm border. */
export const SecondaryButton = ({ children, onClick, type = 'button', disabled, className = '' }) => (
  <button
    type={type}
    onClick={onClick}
    disabled={disabled}
    className={`inline-flex items-center justify-center gap-2 rounded-[10px] px-5 py-2.5 text-[14px] font-display font-bold text-ink bg-white border-[1.5px] border-gray-300 hover:border-ink transition-colors disabled:opacity-50 ${className}`}
  >
    {children}
  </button>
);

/** Content card — white on the bone canvas, warm border, soft shadow. */
export const AtelierCard = ({ children, className = '', padded = true }) => (
  <div className={`bg-white rounded-xl border border-line shadow-[0_1px_2px_rgba(27,27,27,0.05)] ${padded ? 'p-5 sm:p-6' : ''} ${className}`}>
    {children}
  </div>
);

/** Hero stat — the number is editorial serif, like a magazine figure. */
export const StatCard = ({ label, value, sub, icon, className = '' }) => (
  <AtelierCard className={className}>
    <div className="flex items-start justify-between gap-3">
      <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-gray-400">{label}</p>
      {icon && (
        <span className="inline-grid place-items-center w-9 h-9 rounded-xl flex-shrink-0" style={{ background: `${ORANGE}14`, color: ORANGE }}>
          {icon}
        </span>
      )}
    </div>
    <p className="mt-2 font-fell text-3xl sm:text-[34px] font-semibold text-ink tabular-nums leading-none">{value}</p>
    {sub && <p className="mt-2 text-[12.5px] text-muted">{sub}</p>}
  </AtelierCard>
);

/** Empty state — says what belongs here + ONE way to create it. */
export const EmptyState = ({ icon, title, body, action, className = '' }) => (
  <div className={`text-center py-14 px-6 ${className}`}>
    {icon && (
      <span className="inline-grid place-items-center w-16 h-16 rounded-full mb-5" style={{ background: '#F4F0E9', color: ORANGE }}>
        {icon}
      </span>
    )}
    <h3 className="font-fell text-2xl font-semibold text-ink">{title}</h3>
    {body && <p className="mt-2 text-[14.5px] text-muted max-w-sm mx-auto">{body}</p>}
    {action && <div className="mt-6 flex justify-center">{action}</div>}
  </div>
);

/** Status pill — the ONLY badge style (matches Coupons/Plugin Store). */
export const StatusPill = ({ tone = 'gray', children }) => {
  const tones = {
    green: 'bg-green-100 text-green-700',
    gray: 'bg-gray-100 text-gray-600',
    red: 'bg-red-100 text-red-700',
    amber: 'bg-amber-100 text-amber-700',
    blue: 'bg-blue-100 text-blue-700',
    orange: 'bg-orange-100 text-[#8E0808]',
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11.5px] font-bold ${tones[tone] || tones.gray}`}>
      {children}
    </span>
  );
};

/** Section label inside a card — small caps, warm. */
export const SectionLabel = ({ children, className = '' }) => (
  <p className={`text-[11px] font-bold uppercase tracking-[0.16em] text-gray-400 ${className}`}>{children}</p>
);


/**
 * WindowCard — the Shopfront signature: a panel drawn as a tiny browser
 * window (three dots title bar, ink frame). `pop` adds the hard offset
 * shadow from the logo — use on ONE hero/feature moment per view, never
 * on dense lists.
 */
export const WindowCard = ({ children, title, pop = false, className = '', barRight = null }) => (
  <div className={`win-card ${pop ? 'win-pop' : ''} ${className}`}>
    <div className="win-bar">
      <span className="win-dot win-dot--red" />
      <span className="win-dot win-dot--ink" />
      <span className="win-dot win-dot--cream" />
      {title && (
        <span className="ml-2 text-[10.5px] font-display font-bold uppercase tracking-[0.14em] text-muted truncate">{title}</span>
      )}
      {barRight && <span className="ml-auto">{barRight}</span>}
    </div>
    <div>{children}</div>
  </div>
);

/** OpenBadge — the logo's hanging OPEN sign as a live/status chip. */
export const OpenBadge = ({ children = 'Open', className = '' }) => (
  <span className={`badge-open ${className}`}>{children}</span>
);
